import { ChemistryLitElement } from '@adornis/chemistry/chemistry-tailwind-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { Course } from '@chancenwerk/shared/courses/Course.js';
import type { CourseAbschluss } from '@chancenwerk/shared/courses/CourseAbschluss.js';
import type { KPF } from '@chancenwerk/shared/master-data-management/kpf/entities/KPF.js';
import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { BoardRoutes } from '../routes.js';
import './board-component-course-startdate.js';
import './board-component-flat-loader.js';

/**
 * @customElement board-component-abs-overview-grid
 */
@customElement('board-component-abs-overview-grid')
export class BoardComponentABSOverviewGrid extends ChemistryLitElement {
  @property({ attribute: false }) public kpf: KPF | undefined = undefined;
  @property({ attribute: false }) public courses = new RXController(this, [] as Course[]);
  @property({ attribute: false }) public loading = false;
  @property() public name = '';
  @property() public class = '';

  @property({ type: Boolean }) public noToggle = false;
  @property({ type: Boolean }) public noHeader = false;

  // Is set if location time or day is missing
  @property({ attribute: false }) public hasMissingData = false;

  override render() {
    const content = this.loading || !this.kpf ? this._loader() : this._content();

    if (this.noToggle) {
      return content;
    }

    return html`
      <x-toggle-content
        .label=${html`<cr-h4>${this.name}</cr-h4> ${this.hasMissingData
            ? html`<cr-icon ${css({ color: this.colors.tone.error, fontSize: '1.8rem' })}>
                report_problem
                <cr-tooltip> <cr-text> In dieser ${this.name} fehlen Daten! </cr-text> </cr-tooltip>
              </cr-icon>`
            : nothing}`}
      >
        ${content}
      </x-toggle-content>
    `;
  }

  private _loader() {
    return html` <cr-flex w100 center crossaxis-center> <cr-loader></cr-loader> </cr-flex> `;
  }

  private _content() {
    return html`
      <cr-flex horizontal space="md" space-between crossaxis-center ${css({ overflow: 'auto' })}>
        <cr-flex space="md" center>
          ${this.noHeader
            ? nothing
            : html`<cr-grid columns=${`150px 150px 100px 1fr 1fr 1fr`} space="md">
                <cr-text bold>Fach</cr-text>
                <cr-text bold>Abschluss</cr-text>
                <cr-text bold>Tag</cr-text>
                <cr-text bold>Zeit</cr-text>
                <cr-text bold>DiKu</cr-text>
                <cr-text bold>Jugendliche</cr-text>

                ${this.courses.value.length
                  ? repeat(
                      this.courses.value ?? [],
                      c => c._id,
                      c => this._course(c),
                    )
                  : html`<cr-flex center crossaxis-center horizontal space="sm">
                      <cr-text> Es sind keine Kurse für dieses KPF eingetragen. </cr-text>
                    </cr-flex>`}
              </cr-grid>`}
        </cr-flex>

        ${this.class
          ? html`<cr-flex center crossaxis center>
              <cr-button
                mode="fab"
                icon="chevron_right"
                href=${`${BoardRoutes.details}/${this.class}/${this.kpf?._id}`}
              >
                <cr-tooltip> <cr-text> Details </cr-text> </cr-tooltip>
              </cr-button>
            </cr-flex>`
          : nothing}
      </cr-flex>
    `;
  }

  private _course(course: CourseAbschluss) {
    const produkt = this.kpf?.produkt;
    if (produkt._class !== 'ProductABS') throw new Error('only use with CHANCENabschluss Produkt');

    const diKuPercentage = this._getPercentage(course.diKuIDs?.length ?? 0, 1);
    const osStudentsPercentage = this._getPercentage(course.osStudentIDs.length, course.maxAttendanceCount ?? 0);

    return html`
      ${this._valueOrMissing(!!course.subject, course.subject ?? '', 'Fach')}
      ${this._valueOrMissing(!!course.type, course.type ?? '', 'Abschluss')}
      ${this._valueOrMissing(!!course.weekday, course.weekday ?? '', 'Tag')}
      ${this._valueOrMissing(
        !!(course.startTime && course.endTime),
        `${course.startTime?.toFormat('HH:mm')} - ${course.endTime?.toFormat('HH:mm')}`,
        'Uhrzeit',
      )}
      ${this._percentageBar(true, diKuPercentage, course.diKuIDs?.length ?? 0, 1)}
      ${this._percentageBar(true, osStudentsPercentage, course.osStudentIDs.length, 3)}
    `;
  }

  private _valueOrMissing(hasValue: boolean, value: string | undefined, label: string) {
    return html` <cr-flex center>
      ${hasValue
        ? html` <cr-text> ${value} </cr-text> `
        : html`
            <cr-flex horizontal space="xs">
              <cr-text ${css({ color: this.colors.tone.error })}> ${label} </cr-text>
              <cr-text bold ${css({ color: this.colors.tone.error, fontSize: '1.4rem' })}> ? </cr-text>
            </cr-flex>
          `}
    </cr-flex>`;
  }

  private _percentageBar(visibile: boolean, value: number, count: number, all: number | undefined | null) {
    if (!visibile) {
      return nothing;
    }
    return html`<cr-flex ${css({ alignItems: 'flex-start' })}>
      <cr-flex space="sm">
        <cr-progress-bar
          ${css({ maxWidth: '180px' })}
          .value=${value}
          tone=${this._getProgressBarTone(value)}
        ></cr-progress-bar>
        <cr-text> ${count} / ${all ?? '?'} </cr-text>
      </cr-flex>
    </cr-flex>`;
  }

  private _getPercentage(actualNumber: number, neededNumber: number) {
    if (!actualNumber || !neededNumber) return 0;
    return actualNumber / neededNumber;
  }

  private _getProgressBarTone(percentage: number) {
    if (percentage <= 0.2) return 'error';
    if (percentage < 1.0) return 'warning';
    return 'success';
  }
}
